import {
	Alert,
	AsyncSelect,
	Badge,
	Stack,
	Text,
	Box,
	Button,
	Inline,
	Icons,
	Spinner,
	Table,
	Time,
	Col,
	Divider,
	Grid,
	joinAttributes,
	RelativeTime,
	Money,
	Dropdown,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	utcTimestampToLocalDate,
	getDiff,
	fromNow,
	endOf,
	dateToUTCString,
	formatDate,
	localOrUtcTimestampToLocalDate,
	parseDate,
	setTimeFrom,
	localOrUtcTimeToLocalDate,
} from "@sembark-travel/datetime-utils"
import { isNumeric, withOrdinalSuffix } from "@sembark-travel/number-utils"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import {
	Form,
	SelectField,
	TextInputField,
	SelectInputField,
	validateFormValues,
	SubmissionError,
	withServerErrors,
	GetFieldValue,
	TextAreaInputField,
	useFieldValue,
	arrayMutators,
	EmptyNumberValidator,
	SwitchInputField,
	useForm,
} from "@sembark-travel/ui-form"
import React, { useId, useMemo, useEffect, useRef, useState } from "react"
import useSWR from "swr"
import * as Validator from "yup"
import {
	IBooking,
	TBookingCancellationCharges,
	getDisplayNameForStage,
	getDescriptionForStage,
	stageToDisplayNameMapping,
} from "./store"
import { EditHotelBookingDetailsInDialog } from "./ItemForm"
import { InstalmentsInputField } from "../Payments"
import { TTripDestination } from "../TripDestinations"
import { IHotel, SelectHotelContact } from "../Hotels"
import { IContact } from "../Contacts"
import {
	formatMoneyByDecimal,
	formatMoneyByIntl,
	isZeroMoney,
	moneyParseByDecimal,
	multipleMoneyBy,
	subtractMoney,
} from "@sembark-travel/money"
import { Link } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import {
	ISupplier,
	SelectHotelSuppliers,
	SelectSupplierContact,
	HotelPaymentPreferencesForm,
} from "../Supplier"
import { getRoomStatusOnDate, getCloseDataForHotels } from "./store"
import { showClosedServicesAlert } from "../TravelActivities"
import { SelectHotelPaymentPreferences } from "../SupplierPaymentPreferences"
import { PERMISSIONS, useCheckPermissions } from "../Auth"

interface IInstalment {
	amount: number
	due_at: string
	currency: string
}

function XHR(xhr: XHRInstance) {
	return {
		async changeHotelBookingStage(
			bookingIds: Array<number>,
			stageId: number,
			comments?: string
		): Promise<void> {
			return xhr.patch("/trip-hotel-booking-stages", {
				items: bookingIds,
				stage: stageId,
				comments,
			})
		},
		async getBookingMethods(
			params: unknown
		): Promise<Array<{ id: string; name: string }>> {
			return xhr
				.get(`/hotel-booking-methods`, { params })
				.then((resp) => resp.data.data)
		},
		async getBookingDropReasons(
			params: unknown
		): Promise<Array<{ id: string; name: string }>> {
			return xhr
				.get(`/hotel-booking-drop-reasons`, { params })
				.then((resp) => resp.data.data)
		},
		async getCancellationCharges(
			bookingId: number
		): Promise<TBookingCancellationCharges> {
			return xhr
				.get(`/hotel-bookings/${bookingId}/cancellation-charges`)
				.then((resp) => resp.data.data)
		},
		async getBookingChangeReasons(
			params: unknown
		): Promise<Array<{ id: string; name: string }>> {
			return xhr
				.get(`/hotel-booking-change-reasons`, { params })
				.then((resp) => resp.data.data)
		},
		async getBookingStageTags(
			params: unknown
		): Promise<Array<{ id: string; name: string }>> {
			return xhr
				.get(`/hotel-booking-stage-tags`, { params })
				.then((resp) => resp.data.data)
		},
		async getInstalments(
			quoteId: string | number,
			supplierId?: string | number
		): Promise<{
			data: IInstalment[]
			meta: { total: number; currency: string }
		}> {
			return xhr
				.get(`/hotel-bookings/${quoteId}/instalment-presets`, {
					params: {
						supplier_id: supplierId,
					},
				})
				.then((resp) => resp.data)
		},
	}
}

export function HotelBookingCurrentStage({
	booking,
	onChangeSuccess,
	tripStartDate: startDate,
	tripEndDate: endDate,
	minimal,
	tripDestinations,
	readOnly,
}: Readonly<{
	booking: IBooking
	onChangeSuccess?: () => void
	tripStartDate: Date
	tripEndDate: Date
	minimal?: boolean
	tripDestinations: Array<TTripDestination>
	readOnly?: boolean
}>) {
	const {
		booking_stage,
		is_shared,
		booking_method,
		change_reason,
		drop_reason,
	} = booking
	const { state, created_by, created_at, tag, comments } = booking_stage
	return (
		<Box>
			<Box display="inlineFlex" alignItems="center" whiteSpace="preserve">
				<Box
					marginRight="1"
					title={
						created_at && created_by
							? `By ${created_by.name}, ${fromNow(
									utcTimestampToLocalDate(created_at)
								)}`
							: undefined
					}
				>
					{state === "booked" ? (
						<Box display="inlineBlock" marginRight="1">
							<Icons.OkCircleSolid color="success" />
						</Box>
					) : null}
					<Box as="span" fontWeight="semibold">
						{getDisplayNameForStage(state)}
					</Box>{" "}
					{is_shared ? null : (
						<>
							{state === "booked" ? (
								<Box
									display="inline"
									fontSize="xs"
									fontWeight="normal"
									marginLeft="1"
									color="muted"
								>
									{booking_method}
								</Box>
							) : state === "changed" ? (
								<Box
									display="inline"
									fontSize="xs"
									fontWeight="normal"
									marginLeft="1"
									color="muted"
								>
									{change_reason}
								</Box>
							) : state === "dropped" ? (
								<Box
									display="inline"
									fontSize="xs"
									fontWeight="normal"
									marginLeft="1"
									color="muted"
								>
									{drop_reason}
								</Box>
							) : null}
						</>
					)}
					{!minimal && tag && !is_shared ? (
						<Box marginTop="1">
							<Badge primary outlined>
								{tag}
							</Badge>
						</Box>
					) : null}
				</Box>
				<HotelBookingStageChange
					tripDestinations={tripDestinations}
					booking={booking}
					onChangeSuccess={onChangeSuccess}
					tripStartDate={startDate}
					tripEndDate={endDate}
					key={state}
					minimal={minimal}
					readOnly={readOnly}
				/>
			</Box>
			{!minimal ? (
				<Box fontSize="sm" color="muted" marginTop="2">
					{comments && !is_shared ? (
						<blockquote style={{ maxHeight: "100px", overflow: "auto" }}>
							{comments}
						</blockquote>
					) : null}
					{created_by && created_at
						? joinAttributes(
								`by ${created_by.name}`,
								<RelativeTime value={utcTimestampToLocalDate(created_at)} />
							)
						: null}
				</Box>
			) : null}
		</Box>
	)
}

export function HotelBookingStageChange({
	booking,
	onChangeSuccess,
	tripStartDate: startDate,
	tripEndDate: endDate,
	minimal,
	tripDestinations,
	readOnly,
}: Readonly<{
	booking: IBooking
	onChangeSuccess?: () => void
	tripStartDate: Date
	tripEndDate: Date
	minimal?: boolean
	tripDestinations: Array<TTripDestination>
	readOnly?: boolean
}>) {
	const xhr = useXHR()
	const { id, booking_stage, can_modify } = booking
	const { state } = booking_stage
	const [isOpen, openDialog, closeDialog] = useDialog()
	const canEdit = useMemo(() => {
		const nextStages = getNextStages(state)
		return Boolean(nextStages.length && !readOnly && can_modify)
	}, [state, readOnly, can_modify])
	if (!canEdit) return null
	return (
		<>
			<Button
				onClick={openDialog}
				{...(minimal ? { level: "tertiary", inline: true } : {})}
				title="Update Booking Status"
			>
				{minimal ? <Icons.Pencil /> : "Update Status"}
			</Button>
			<Dialog open={isOpen} onClose={closeDialog} title="Update Booking Status">
				<HotelBookingStageChangeForm
					tripDestinations={tripDestinations}
					booking={booking}
					onChangeSuccess={onChangeSuccess}
					tripStartDate={startDate}
					tripEndDate={endDate}
					onSubmit={async (values) => {
						const resp = await xhr.patch(
							`/hotel-bookings/${id}/active-stage`,
							values
						)
						showSnackbar("Hotel Booking status updated.")
						onChangeSuccess && onChangeSuccess()
						closeDialog()
						const booking: IBooking = resp.data.data
						return booking
					}}
					onCancel={closeDialog}
				/>
			</Dialog>
		</>
	)
}

function getNextStages(
	currentState: IBooking["booking_stage"]["state"]
): Array<typeof currentState> {
	switch (currentState) {
		// Hotel CRM: booking happens only via Send Booking; manual change is drop-only
		case "booked":
			return ["dropped"]
		case "initialized":
		case "in_progress":
		case "changed":
		case "dropped":
		case "trip_dropped":
		default:
			return []
	}
}

interface IBookingStageChangeData {
	nextStage?: IBooking["booking_stage"]["state"]
	tag?: { id: number; name: string }
	booking_confirmation_id?: string
	hotel_contact?: IContact
	supplier_contact?: IContact
	booking_supplier?: ISupplier
	direct_booking?: 1 | 0
	change_reason?: { name: string }
	drop_reason?: { name: string }
	comments?: string
	instalments?: Array<{ due_at: Date; amount: number }>
	cancellation_charges?: number
}

function getValidate(bookedPrice?: number) {
	return validateFormValues(
		Validator.object().shape({
			nextStage: Validator.string().required("Please select a status"),
			booking_confirmation_id: Validator.string().when(
				"nextStage",
				([nextStage], schema: Validator.StringSchema) =>
					nextStage === "booked"
						? schema
								.trim()
								.required("Please provide booking confirmation number")
						: schema
			),
			booking_supplier: Validator.object()
				.nullable()
				.when(["nextStage", "direct_booking"], {
					is: (
						nextStage: IBookingStageChangeData["nextStage"],
						direct_booking: IBookingStageChangeData["direct_booking"]
					) => nextStage === "booked" && !direct_booking,
					then: (schema) =>
						schema.required(
							"Please select a supplier or click on direct hotel booking"
						),
					otherwise: (schema) => schema.notRequired(),
				}),
			change_reason: Validator.object().when(
				"nextStage",
				([nextStage], schema: Validator.AnyObjectSchema) =>
					nextStage === "changed"
						? schema.required("Please select a reason for hotel change")
						: schema
			),
			drop_reason: Validator.object().when(
				"nextStage",
				([nextStage], schema: Validator.AnyObjectSchema) =>
					nextStage === "dropped"
						? schema.required("Please select a reason for hotel drop")
						: schema
			),
			instalments: Validator.array()
				.nullable()
				.of(
					Validator.object().shape({
						amount: EmptyNumberValidator().required(
							"Please provide the amount for instalment"
						),
						due_at: Validator.date().required(
							"Please provide a due date for instalment"
						),
					})
				),
			comments: Validator.string(),
			cancellation_charges: EmptyNumberValidator()
				.nullable()
				.min(0, "Please provide a non-negative value for charge.")
				.when("nextStage", ([nextStage], schema) =>
					nextStage === "dropped" && isNumeric(bookedPrice)
						? schema.max(
								Number(bookedPrice),
								"Drop charges can not be more than the booking amount."
							)
						: schema
				),
		})
	)
}

// Hotel CRM build: booking edit not yet supported for the remote flow — off entirely for now.
const canEditBookingDetails = false

function HotelBookingStageChangeForm({
	booking: minimalBooking,
	onChangeSuccess,
	tripStartDate: startDate,
	tripEndDate: endDate,
	onSubmit,
	onCancel,
	tripDestinations,
}: Readonly<{
	booking: IBooking
	onChangeSuccess?: () => void
	tripStartDate: Date
	tripEndDate: Date
	tripDestinations: Array<TTripDestination>
	onSubmit: (data: {
		booking_confirmation_id?: string | null
		comments?: string | null
		tag?: string
		stage: string
		supplier?: number | null
		change_reason?: string | null
		drop_reason?: string | null
		instalments?: null | Array<{ due_at: string; amount: number }>
		hotel_contact?: number
		supplier_contact?: number
		cancellation_charges?: number
	}) => Promise<IBooking>
	onCancel?: () => void
}>) {
	const { booking_stage } = minimalBooking
	const { state } = booking_stage
	const nextStages = getNextStages(state)
	const [refreshId, setRefreshId] = useState(0)
	const [isFetchingDropCharges, setIsFetchingDropCharges] = useState(false)

	const xhr = useXHR()
	const {
		data: booking,
		error,
		mutate,
	} = useSWR<IBooking>(`/hotel-bookings/${minimalBooking.id}`, () =>
		xhr
			.get(`/hotel-bookings/${minimalBooking.id}`)
			.then((resp) => resp.data.data)
	)
	const validate = useMemo(
		() => getValidate(booking?.booked_price),
		[booking?.booked_price]
	)
	const initialValues: IBookingStageChangeData = useMemo(() => {
		return {
			nextStage: undefined,
			tag: undefined,
			comments: "",
			booking_confirmation_id: undefined,
			booking_supplier: undefined,
			direct_booking: 0,
			change_reason: undefined,
			drop_reason: undefined,
			cancellation_charges: undefined,
		}
	}, [])
	if (error && !booking) {
		return <Alert status="error">{error.message}</Alert>
	}
	if (!booking) {
		return <Spinner alignCenter padding="4" />
	}
	return (
		<Form<IBookingStageChangeData>
			initialValues={initialValues}
			validate={validate}
			onSubmit={withServerErrors(async (values) => {
				if (!values.nextStage) {
					throw new Error("Please select a next stage")
				}
				if (values.nextStage === "booked") {
					const closeHotelData: Record<string, Date[]> = {}
					booking.room_nights?.forEach((roomNight) =>
						booking.hotel.room_types.forEach((roomType) => {
							if (roomType.id === roomNight.room_type.id) {
								const dates = roomNight.dates.map((date) => parseDate(date))
								getCloseDataForHotels(
									roomType,
									dates,
									`${booking.hotel.name} - ${roomType.name}`,
									closeHotelData
								)
							}
						})
					)
					if (Object.keys(closeHotelData).length > 0) {
						const alertMessage = showClosedServicesAlert(closeHotelData)
						throw new Error(
							"The following rooms are sold out on the selected dates:\n" +
								alertMessage +
								"\nPlease adjust the booking details accordingly."
						)
					}
				}
				const {
					nextStage,
					booking_supplier,
					change_reason,
					drop_reason,
					tag,
					instalments,
					cancellation_charges,
					hotel_contact,
					supplier_contact,
					direct_booking,
					...otherData
				} = values
				await onSubmit({
					...otherData,
					tag: tag ? tag.name : undefined,
					stage: nextStage,
					supplier: booking_supplier ? booking_supplier.id : null,
					change_reason: change_reason ? change_reason.name : null,
					drop_reason: drop_reason ? drop_reason.name : null,
					hotel_contact: hotel_contact?.id,
					supplier_contact: booking_supplier ? supplier_contact?.id : undefined,
					instalments: instalments
						? instalments.map((d) => ({
								amount: d.amount,
								due_at: dateToUTCString(endOf(d.due_at, "day")),
								due_at_local: formatDate(d.due_at, "YYYY-MM-DD"),
							}))
						: null,
					cancellation_charges: Number(cancellation_charges || 0),
				})
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<GetFieldValue<IBookingStageChangeData["nextStage"]> name="nextStage">
						{({ value: nextStage }) => (
							<Dialog.Body>
								<Stack gap="4">
									<Grid gap="4">
										<Col sm="auto" xs={12}>
											<SelectInputField name="nextStage" label="Select Status">
												<option value="">Select status...</option>
												{nextStages.map((state) => (
													<option
														key={state}
														title={getDescriptionForStage(state)}
														value={state}
													>
														{getDisplayNameForStage(state)}
													</option>
												))}
											</SelectInputField>
										</Col>
										<Col key={nextStage}>
											{nextStage === "in_progress" || nextStage === "booked" ? (
												<SelectField
													stage={
														nextStage === "booked" ? "booked" : "in_progress"
													}
													label="Tag"
													select={SelectHotelBookingStageTags}
													name="tag"
													searchable={false}
												/>
											) : nextStage === "dropped" ? (
												<SelectField
													name="drop_reason"
													select={SelectHotelBookingDropReason}
													label="Select Dropping Reason"
												/>
											) : nextStage === "changed" ? (
												<SelectField
													name="change_reason"
													select={SelectHotelBookingChangeReason}
													label="Select a reason for Change"
												/>
											) : null}
										</Col>
									</Grid>
									{nextStage === "dropped" ? (
										<DropChargesField
											booking={booking}
											onFetchingChange={setIsFetchingDropCharges}
										/>
									) : null}
									<Divider sm />
									{nextStage === "booked" ? (
										<Stack gap="4">
											<Stack gap="4">
												<Alert status="warning">
													<Text>
														Please verify following booking details once again.
														These details <strong>CAN NOT BE</strong> modified
														after booking.
													</Text>
												</Alert>
												<Box
													rounded="lg"
													bgColor="primary"
													borderWidth="1"
													borderColor="primary"
													padding="4"
												>
													<Stack gap="8">
														<BookingOverview booking={booking} />
														<Grid gap="4">
															<Col xs={12} sm={12}>
																<TitleValue title="Booked Price">
																	<Stack gap="2">
																		<Text fontSize="xl" fontWeight="semibold">
																			<Money
																				amount={booking.booked_price || 0}
																				currency={booking.currency}
																				showCurrency
																			/>
																		</Text>
																		<Text fontSize="sm">
																			This price should match to the booking
																			price that is confirmed with the hotel.
																		</Text>
																	</Stack>
																</TitleValue>
															</Col>
														</Grid>
													</Stack>
													{canEditBookingDetails ? (
														<>
															<Divider sm />
															<Box>
																<EditHotelBookingDetailsInDialog
																	tripDestinations={tripDestinations}
																	booking={booking}
																	startDate={startDate}
																	endDate={endDate}
																	level="primary"
																	onSuccess={() => {
																		onChangeSuccess && onChangeSuccess()
																		mutate()
																		setRefreshId((id) => id + 1)
																	}}
																/>{" "}
																if above information is not correct.
															</Box>
														</>
													) : null}
												</Box>
											</Stack>
											<Text color="muted">
												If above details are correct, please proceed to fill
												following fields.
											</Text>
											<Stack gap="1">
												<label htmlFor="booking_supplier">
													Select Supplier
												</label>
												<Inline gap={"4"} collapseBelow="sm">
													<Box
														paddingY="2"
														paddingX="4"
														borderWidth="1"
														rounded="md"
														bgColor="subtle"
														style={{ maxHeight: "40px" }}
													>
														<SwitchInputField
															name="direct_booking"
															label="Direct Hotel Booking"
															onChange={(value) => {
																if (value) {
																	form.batch(() => {
																		form.change("booking_supplier", undefined)
																		form.change("direct_booking", 1)
																		setRefreshId((id) => id + 1)
																	})
																} else {
																	form.change("direct_booking", 0)
																}
															}}
														/>
													</Box>
													<SelectField
														name="booking_supplier"
														select={SelectHotelSuppliers}
														creatable
														defaultOptions={
															booking?.suggested_suppliers || undefined
														}
														onChange={(value: ISupplier) => {
															if (value) {
																form.batch(() => {
																	form.change("booking_supplier", value)
																	form.change("direct_booking", 0)
																	form.change("supplier_contact", undefined)
																	form.change("instalments", undefined)
																})
																if (value.hotel_supplier_payment_preference) {
																	setRefreshId((id) => id + 1)
																}
															} else {
																form.batch(() => {
																	form.change("booking_supplier", undefined)
																	form.change("supplier_contact", undefined)
																	form.change("instalments", undefined)
																})
															}
														}}
													/>
												</Inline>
											</Stack>
											<Inline gap={"2"} collapseBelow="md">
												<GetFieldValue<
													ISupplier | undefined
												> name="booking_supplier">
													{({ value }) => {
														return (
															<>
																{value ? (
																	<SelectField
																		label="Booking Confirmed by Supplier Contact Person"
																		secondaryLabel="optional"
																		select={SelectSupplierContact}
																		name="supplier_contact"
																		supplierId={value.id}
																		creatable
																		fetchOnMount
																		help="Contact details will not be included in the Voucher PDF"
																	/>
																) : null}
																<SelectField
																	label={
																		value
																			? "Hotel's Contact Person"
																			: "Booking Confirmed by Hotel's Contact Person"
																	}
																	secondaryLabel="optional"
																	select={SelectHotelContact}
																	name="hotel_contact"
																	hotelId={booking.hotel_id}
																	creatable
																	fetchOnMount
																	help="Contact details will be included in the Voucher PDF"
																/>
															</>
														)
													}}
												</GetFieldValue>
											</Inline>

											<Box maxWidth="md">
												<TextInputField
													label="Booking Confirmation Number"
													name="booking_confirmation_id"
													type="text"
													placeholder="BKN123F1"
													help="Confirmation/Reference number provided by the hotel"
												/>
											</Box>
											<BookingPaymentDetails
												booking={booking}
												onChange={() => {
													mutate()
													setRefreshId((id) => id + 1)
												}}
												refreshId={refreshId}
											/>
										</Stack>
									) : null}
									<TextInputField
										label="Any Comments"
										secondaryLabel="optional"
										name="comments"
										type="text"
									/>
								</Stack>
							</Dialog.Body>
						)}
					</GetFieldValue>
					<Dialog.Footer>
						<Stack gap="4">
							<SubmissionError />
							<Inline gap="4">
								<GetFieldValue<
									IBookingStageChangeData["nextStage"]
								> name="nextStage">
									{({ value: nextStage }) => (
										<Button
											type="submit"
											status={nextStage === "dropped" ? "danger" : undefined}
											disabled={submitting || isFetchingDropCharges}
										>
											{submitting
												? "Updating..."
												: isFetchingDropCharges
													? "Fetching drop charges..."
													: nextStage === "dropped"
														? "Drop Booking"
														: "Update"}
										</Button>
									)}
								</GetFieldValue>
								<Button onClick={onCancel} disabled={submitting}>
									Cancel
								</Button>
							</Inline>
						</Stack>
					</Dialog.Footer>
				</form>
			)}
		</Form>
	)
}

function DropChargesField({
	booking,
	onFetchingChange,
}: Readonly<{
	booking: IBooking
	onFetchingChange: React.Dispatch<React.SetStateAction<boolean>>
}>) {
	const xhr = useXHR()
	const form = useForm()
	const { hasPermission } = useCheckPermissions()
	const { id, currency, paid_payments_amount, booked_price } = booking
	const {
		data: suggested,
		error,
		isValidating,
		mutate: refetch,
	} = useSWR(
		`/hotel-bookings/${id}/cancellation-charges`,
		() => XHR(xhr).getCancellationCharges(id),
		{ revalidateOnFocus: false }
	)

	const isFetching = !suggested && (!error || isValidating)

	const suggestedMoney = useMemo(
		() =>
			suggested
				? moneyParseByDecimal(
						Number(suggested.cancellation_amount || 0),
						suggested.currency
					)
				: undefined,
		[suggested]
	)
	const { value } = useFieldValue<number | undefined>("cancellation_charges")

	useEffect(() => {
		onFetchingChange(isFetching)
		return () => onFetchingChange(false)
	}, [isFetching, onFetchingChange])

	useEffect(() => {
		if (!suggestedMoney) return
		form.change(
			"cancellation_charges",
			Number(formatMoneyByDecimal(suggestedMoney))
		)
	}, [suggestedMoney, form])

	const enteredMoney = moneyParseByDecimal(Number(value || 0), currency)
	const isOverridden = suggestedMoney
		? moneyParseByDecimal(Number(value || 0), suggestedMoney.currency)
				.amount !== suggestedMoney.amount
		: false
	const balance = subtractMoney(
		moneyParseByDecimal(Number(paid_payments_amount || 0), currency),
		enteredMoney
	)
	const policy = suggested?.cancellation_policy
	const policyName = policy?.head_name || "Applied"

	return (
		<Stack gap="3" padding="4" borderWidth="1" rounded="md">
			<Stack gap="1">
				<Inline gap="2" alignItems="baseline">
					<label htmlFor="field_cancellation_charges">Drop Charges</label>
					<Box fontSize="sm" color="muted">
						({currency})
					</Box>
				</Inline>
				{isFetching ? (
					<Inline gap="1" alignItems="center" fontSize="sm" color="muted">
						<Spinner /> Fetching charges as per cancellation policy...
					</Inline>
				) : error ? (
					<Inline gap="2" alignItems="center" fontSize="sm" color="muted">
						<Text>
							Could not fetch charges as per cancellation policy. Retry or enter
							the amount manually.
						</Text>
						<Button size="sm" level="tertiary" onClick={() => refetch()}>
							<Icons.Refresh /> Retry
						</Button>
					</Inline>
				) : policy ? (
					<Text fontSize="sm" color="muted">
						As per{" "}
						{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
							<Link
								color="accent"
								target="_blank"
								rel="noopener noreferrer"
								to={`${generatePath(
									"/cancellation-policy/:cancellationPolicyId",
									{
										cancellationPolicyId: String(policy.head_id),
									}
								)}?${new URLSearchParams({
									name: policyName,
									version_id: String(policy.id),
									version: String(policy.version),
								}).toString()}`}
							>
								<strong>{policyName}</strong>
							</Link>
						) : (
							<strong>{policyName}</strong>
						)}{" "}
						cancellation policy
					</Text>
				) : null}
			</Stack>
			<Stack gap="1">
				<Inline gap="2" alignItems="center">
					<TextInputField
						name="cancellation_charges"
						type="number"
						placeholder="e.g. 0.00"
						maxWidth="xs"
						disabled={isFetching}
					/>
					{isOverridden && suggestedMoney ? (
						<Button
							size="sm"
							level="tertiary"
							onClick={() =>
								form.change(
									"cancellation_charges",
									Number(formatMoneyByDecimal(suggestedMoney))
								)
							}
						>
							Reset to <Money money={suggestedMoney} showCurrency />
						</Button>
					) : null}
				</Inline>
				{isOverridden && suggestedMoney ? (
					<Text fontSize="sm" color="warning">
						As per policy drop charges are{" "}
						<Money money={suggestedMoney} showCurrency />. You entered{" "}
						<Money money={enteredMoney} showCurrency />.
					</Text>
				) : null}
			</Stack>
			<Stack gap="1" fontSize="sm" style={{ maxWidth: "360px" }}>
				<Inline gap="4" justifyContent="between">
					<Text color="muted">Booking</Text>
					<Money amount={booked_price || 0} currency={currency} showCurrency />
				</Inline>
				<Inline gap="4" justifyContent="between">
					<Text color="muted">Paid</Text>
					<Money
						amount={Number(paid_payments_amount || 0)}
						currency={currency}
						showCurrency
					/>
				</Inline>
				<Inline gap="4" justifyContent="between">
					<Text color="muted">Drop charges</Text>
					<Text>
						− <Money money={enteredMoney} showCurrency />
					</Text>
				</Inline>
				<Inline
					gap="4"
					justifyContent="between"
					fontWeight="semibold"
					borderTopWidth="1"
					paddingTop="1"
				>
					{isZeroMoney(balance) ? (
						<Text color="muted">Settled</Text>
					) : (
						<Text color={balance.amount > 0 ? "success" : "danger"}>
							{balance.amount > 0 ? "Receivable" : "Payable Due"}
						</Text>
					)}
					<Money
						money={balance.amount < 0 ? multipleMoneyBy(balance, -1) : balance}
						showCurrency
					/>
				</Inline>
			</Stack>
		</Stack>
	)
}

function InstalmentsData({
	bookingId,
	supplierId,
	checkin,
	checkout,
}: Readonly<{
	bookingId: number | string
	supplierId?: number | string
	checkin: string
	checkout: string
}>) {
	const id = useId()
	const xhr = useXHR()
	const {
		data: instalmentPresets,
		isLoading,
		isValidating,
	} = useSWR(
		`/hotel-bookings/${bookingId}/instalment-presets?${id}/supplier_id=${supplierId}`,
		() => XHR(xhr).getInstalments(bookingId, supplierId),
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
		}
	)
	const { value, onChange } = useFieldValue("instalments")
	const hasSetValue = useRef(false)
	useEffect(() => {
		if (instalmentPresets && !hasSetValue.current) {
			const fieldData = (instalmentPresets?.data || []).map(
				({ amount, due_at }) => ({
					amount: Number(amount),
					due_at: endOf(utcTimestampToLocalDate(due_at), "day"),
					percentage: Number.parseFloat(
						((amount * 100) / (instalmentPresets?.meta?.total || 1)).toFixed(1)
					),
				})
			)
			onChange(fieldData)
			hasSetValue.current = true
		}
	}, [instalmentPresets, onChange])
	if (!value || !hasSetValue.current || isLoading || isValidating) {
		return <Box>Loading instalments</Box>
	}
	return (
		<Stack gap="2">
			<Inline gap="2" flexWrap="wrap">
				<Text fontWeight="semibold" fontSize="md">
					Payment Instalments
				</Text>
				<Inline gap="2" flexWrap="wrap" color="warning">
					<Text>
						(
						<Text as="span" fontWeight="semibold">
							Checkin:
						</Text>{" "}
						<Time timestamp={checkin} format="ddd Do MMM, YYYY" />,
					</Text>
					<Text>
						<Text as="span" fontWeight="semibold">
							Checkout:
						</Text>{" "}
						<Time timestamp={checkout} format="ddd Do MMM, YYYY" />)
					</Text>
				</Inline>
			</Inline>
			<InstalmentsInputField
				name="instalments"
				totalAmount={Number(instalmentPresets?.meta?.total || 1)}
				currency={instalmentPresets?.meta?.currency || "INR"}
			/>
		</Stack>
	)
}

function BookingOverview({
	booking,
	nightsRelativeTo: propNightsRelativeTo,
}: Readonly<{
	booking: IBooking
	nightsRelativeTo?: Date
}>) {
	const nightsRelativeTo: Date =
		propNightsRelativeTo ||
		localOrUtcTimestampToLocalDate(booking.checkin_local, booking.checkin)
	return (
		<Stack gap="4">
			<Inline gap="8" flexWrap="wrap">
				<TitleValue title="Hotel">
					<Text fontWeight="semibold">{booking.hotel.name}</Text>
					<Text fontSize="sm">
						{joinAttributes(
							booking.hotel.address?.location?.name,
							booking.hotel.stars_string
						)}
					</Text>
				</TitleValue>
				<TitleValue title="Checkin">
					<Time
						value={setTimeFrom(
							localOrUtcTimestampToLocalDate(
								booking.checkin_local,
								booking.checkin
							),
							localOrUtcTimeToLocalDate(
								booking.hotel.checkin_at_local,
								booking.hotel.checkin_at
							)
						)}
						dateFormat={true}
						timeFormat="HH:mm"
					/>
				</TitleValue>
				<TitleValue title="Checkout">
					<Time
						value={setTimeFrom(
							localOrUtcTimestampToLocalDate(
								booking.checkout_local,
								booking.checkout
							),
							localOrUtcTimeToLocalDate(
								booking.hotel.checkout_at_local,
								booking.hotel.checkout_at
							)
						)}
						dateFormat={true}
						timeFormat="HH:mm"
					/>{" "}
					({booking.no_of_nights}N)
				</TitleValue>
			</Inline>
			<Stack gap="8">
				{booking.details && booking.details.length ? (
					<Table
						headers={["Nights", "Accommodation", `Price (${booking.currency})`]}
						responsive
						bordered
						rows={booking.details.map((bookingDetail) => {
							const {
								meal_plan,
								room_type,
								no_of_rooms,
								adults_with_extra_bed,
								children_with_extra_bed,
								children_without_extra_bed,
								per_room_booked_price,
								per_adult_with_extra_bed_booked_price,
								per_child_with_extra_bed_booked_price,
								per_child_without_extra_bed_booked_price,
								room_configuration,
								booked_price,
								date,
							} = bookingDetail
							const night =
								getDiff(
									localOrUtcTimestampToLocalDate(
										bookingDetail.date_local,
										bookingDetail.date
									),
									nightsRelativeTo,
									"days"
								) + 1
							const status = getRoomStatusOnDate(
								{
									black_out_date_intervals: room_type.black_out_date_intervals,
								},
								parseDate(date)
							)
							return [
								<Stack key={`${bookingDetail.id}`} gap="1">
									<Text>{withOrdinalSuffix(night)} Night</Text>
									<Text>{meal_plan.name}</Text>
								</Stack>,
								<Box display="inline">
									{joinAttributes(
										<Box display="inline">
											<Text>
												{no_of_rooms} {room_type.name}{" "}
												{status === "stop_sale" && (
													<Badge danger>Unavailable</Badge>
												)}
												{status === "blackout" && (
													<Badge warning>High Demand</Badge>
												)}
											</Text>
											<Text fontSize="sm" whiteSpace="preserveLine">
												(
												{room_configuration?.description?.replace(
													/\+/g,
													"\n+ "
												)}
												)
											</Text>
										</Box>
									)}
								</Box>,
								<Box>
									<Stack>
										{per_room_booked_price ? (
											<Stack>
												<Inline>
													<Text>
														{formatMoneyByIntl(
															moneyParseByDecimal(
																per_room_booked_price,
																booking.currency
															),
															{ showSolidus: true }
														)}
													</Text>
													<Text>
														Per Room {room_configuration?.per_room_details}
													</Text>
												</Inline>
												{adults_with_extra_bed ? (
													<Inline>
														<Text>
															{formatMoneyByIntl(
																moneyParseByDecimal(
																	per_adult_with_extra_bed_booked_price || 0,
																	booking.currency
																),
																{ showSolidus: true }
															)}
														</Text>
														<Text>
															Per Adult with Extra Bed{" "}
															{
																room_configuration?.per_adult_with_extra_bed_details
															}
														</Text>
													</Inline>
												) : null}
												{children_with_extra_bed ? (
													<Inline>
														<Text>
															{formatMoneyByIntl(
																moneyParseByDecimal(
																	per_child_with_extra_bed_booked_price || 0,
																	booking.currency
																),
																{ showSolidus: true }
															)}
														</Text>
														<Text>
															Per Child with Extra Bed{" "}
															{
																room_configuration?.per_child_with_extra_bed_details
															}
														</Text>
													</Inline>
												) : null}
												{children_without_extra_bed ? (
													<Inline>
														<Text>
															{formatMoneyByIntl(
																moneyParseByDecimal(
																	per_child_without_extra_bed_booked_price || 0,
																	booking.currency
																),
																{ showSolidus: true }
															)}
														</Text>
														<Text>
															Per Child without Extra Bed{" "}
															{
																room_configuration?.per_child_without_extra_bed_details
															}
														</Text>
													</Inline>
												) : null}
											</Stack>
										) : (
											<Box>{booked_price}</Box>
										)}
									</Stack>
								</Box>,
							]
						})}
					/>
				) : null}
				{booking.extras && booking.extras.length ? (
					<TitleValue title="Extra Services">
						<Box>
							{booking.extras.map((extra) => {
								const { date, date_local, service } = extra
								const night = date
									? getDiff(
											localOrUtcTimestampToLocalDate(date_local, date),
											localOrUtcTimestampToLocalDate(
												booking.checkin_local,
												booking.checkin
											),
											"days"
										) + 1
									: undefined
								return (
									<Box key={`${extra.id}-${night}`}>
										{night ? (
											<Box display="inline">
												{withOrdinalSuffix(night)} Night
												{" - "}
											</Box>
										) : null}
										<Box display="inline">{joinAttributes(service.name)}</Box>
									</Box>
								)
							})}
						</Box>
					</TitleValue>
				) : null}
			</Stack>
		</Stack>
	)
}

function TitleValue({
	title,
	children,
}: Readonly<{
	title: React.ReactNode
	children: React.ReactNode
}>) {
	return (
		<Stack gap="1">
			<Text
				textTransform="uppercase"
				letterSpacing="wider"
				fontSize="sm"
				fontWeight="semibold"
				color="muted"
			>
				{title}
			</Text>
			<Box fontSize="md">{children}</Box>
		</Stack>
	)
}

export function SelectHotelBookingMethod(
	props: Readonly<Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">>
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			cacheKey="hotel-booking-methods"
			fetchOnMount
			creatable
			createOptionLabel={(q) => `Add "${q}"`}
			fetch={(q) => XHR(xhr).getBookingMethods({ q })}
		/>
	)
}

export function SelectHotelBookingChangeReason(
	props: Readonly<Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">>
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			cacheKey="hotel-booking-change-reasons"
			fetchOnMount
			creatable
			createOptionLabel={(q) => `Add "${q}"`}
			fetch={(q) => XHR(xhr).getBookingChangeReasons({ q })}
		/>
	)
}

export function SelectHotelBookingDropReason(
	props: Readonly<Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">>
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			cacheKey="hotel-booking-drop-reasons"
			fetchOnMount
			creatable
			createOptionLabel={(q) => `Add "${q}"`}
			fetch={(q) => XHR(xhr).getBookingDropReasons({ q })}
		/>
	)
}

export function SelectHotelBookingStageTags({
	stage,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	stage?: keyof typeof stageToDisplayNameMapping
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			cacheKey={`hotel-booking-stage-${stage || "all"}-tags`}
			fetchOnMount
			{...props}
			fetch={(q) => XHR(xhr).getBookingStageTags({ q, stage })}
		/>
	)
}

export function UpdateBookingTagInDialog({
	booking,
	children,
	onSuccess,
}: Readonly<{
	booking: IBooking
	children: (props: { onEdit: () => void }) => React.ReactNode
	onSuccess: () => void
}>) {
	const [isOpen, open, close] = useDialog()
	const initialValues: {
		tag?: { id: number; name: string }
		comments: string
	} = useMemo(() => {
		return {
			tag: undefined,
			comments: "",
		}
	}, [])
	const xhr = useXHR()
	if (
		(booking.booking_stage.state !== "in_progress" &&
			booking.booking_stage.state !== "booked") ||
		!booking.can_modify
	)
		return null
	return (
		<>
			{children({ onEdit: open })}
			<Dialog open={isOpen} onClose={close} sm title="Edit Tag/Comments">
				<Dialog.Body>
					<Form<typeof initialValues>
						initialValues={initialValues}
						onSubmit={withServerErrors(async (values) => {
							await xhr.patch(`/hotel-bookings/${booking.id}/tags`, {
								tag: values.tag?.name,
								comments: values.comments,
							})
							close()
							onSuccess()
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									<SubmissionError />
									<Box>
										<SelectField
											label="Select Tag"
											name="tag"
											select={SelectHotelBookingStageTags}
											stage={booking.booking_stage.state}
											secondaryLabel="optional"
											searchable={false}
										/>
									</Box>
									<TextAreaInputField
										name="comments"
										label="Any Comments"
										secondaryLabel="optional"
										placeholder="Provide any additional comments if necessary"
									/>
								</Stack>
								<Divider sm />
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Please wait..." : "Save"}
									</Button>
									<Button onClick={() => close()} disabled={submitting}>
										Cancel
									</Button>
								</Inline>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type PaymentPreferanceProps = {
	supplier?: ISupplier
	onChange: () => void
	hotel: IHotel
}

function PaymentPreferance({
	supplier,
	hotel,
	onChange,
}: PaymentPreferanceProps) {
	return supplier ? (
		<Box
			paddingY="2"
			paddingX="4"
			borderWidth="1"
			rounded="md"
			bgColor="subtle"
		>
			<HotelPaymentPreferencesForm
				supplierId={supplier.id}
				onChange={onChange}
			/>
		</Box>
	) : (
		<Box
			paddingY="2"
			paddingX="4"
			borderWidth="1"
			rounded="md"
			bgColor="subtle"
		>
			<HotelPaymentPreferences hotel={hotel} onChange={onChange} />
		</Box>
	)
}

function HotelPaymentPreferences({
	hotel,
	onChange,
}: Readonly<{
	hotel: IBooking["hotel"]
	onChange: () => void
}>) {
	const { hasPermission } = useCheckPermissions()
	const xhr = useXHR()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	return (
		<TitleValue title="Payment Preference">
			{hotel.payment_preference ? (
				<Inline justifyContent="between" gap="2">
					<Inline gap="4">
						<Text fontSize="md" fontWeight="semibold">
							{hotel.payment_preference?.name}
						</Text>
					</Inline>
					<Box>
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary" size="sm">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuItem onClick={openDialog}>
									<Icons.Pencil /> Edit
								</Dropdown.MenuItem>
							</Dropdown.Menu>
						</Dropdown>
					</Box>
				</Inline>
			) : (
				<Stack gap="2">
					<Text fontSize="md" color="muted" fontWeight="semibold">
						Not Set
					</Text>
					<Text>
						You should set payment preference for the hotel to easily create
						instalment accordingly.
					</Text>
					{hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS) ? (
						<Box>
							<Button onClick={openDialog} size="sm" level="primary">
								Set Payment Preference
							</Button>
						</Box>
					) : null}
				</Stack>
			)}
			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Set Payment Preference"
				sm
			>
				<Form<{
					payment_preference: IBooking["hotel"]["payment_preference"]
				}>
					initialValues={{
						payment_preference: hotel.payment_preference,
					}}
					onSubmit={withServerErrors(async (values) => {
						await xhr.patch(`/hotels/${hotel.id}/payment-preference`, {
							payment_preference_id: (
								values.payment_preference as unknown as {
									id: string
								}
							)?.id,
						})
						closeDialog()
						onChange()
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<Box>
										<SelectField
											name="payment_preference"
											select={SelectHotelPaymentPreferences}
											label="Select Payment Preference"
											fetchOnMount
											multiple={false}
										/>
									</Box>
									<SubmissionError />
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Updating..." : "Update"}
								</Button>
								<Button disabled={submitting} onClick={closeDialog}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</TitleValue>
	)
}

function BookingPaymentDetails({
	booking,
	refreshId,
	onChange,
}: Readonly<{
	booking: IBooking
	refreshId: number
	onChange: () => void
}>) {
	const { value: supplier } = useFieldValue<ISupplier | undefined>(
		"booking_supplier"
	)
	const { value: directBooking } = useFieldValue<0 | 1>("direct_booking")

	if (!directBooking && !supplier) return null
	return (
		<>
			<PaymentPreferance
				hotel={booking.hotel}
				supplier={supplier}
				onChange={onChange}
			/>
			<InstalmentsData
				bookingId={booking.id}
				supplierId={supplier?.id}
				key={refreshId}
				checkin={booking.checkin}
				checkout={booking.checkout}
			/>
		</>
	)
}
